import React, { useMemo } from "react";
import PropTypes from "prop-types";

import LightSelect from "../../CompanyView/LightSelect";
import {
  emptyCombinedPay,
  sanitizeCombinedPay,
} from "../../../components/SalesBonus/moneyRules";
import { companyTodayStr, formatSetupDay } from "../../../helpers/companyTime";
import "./customize.css";

/**
 * Multi-workspace Anticipated earnings Setup — ONE rule list, not a copy of
 * Commission / Bonuses / Flat per workspace.
 *
 * Each bonus (or commission) names a rate and, per selected workspace, which
 * metric feeds it — same shape as MetricPlanEditor columns. Aggregation is
 * sum or average across those workspace metrics, then × rate.
 */

const mkKey = (prefix) => `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;

const blankBonus = (wsIds) => ({
  key: mkKey("b"),
  label: "",
  amount: 1,
  mode: "sum",
  sources: (wsIds || []).reduce((o, id) => ({ ...o, [id]: "" }), {}),
  appliesTo: { preset: "custom", from: "", to: "" },
});

const blankCommission = (wsIds) => ({
  key: mkKey("c"),
  label: "",
  pct: 1,
  mode: "sum",
  sources: (wsIds || []).reduce((o, id) => ({ ...o, [id]: "" }), {}),
  appliesTo: { preset: "custom", from: "", to: "" },
});

const blankFlat = () => ({
  key: mkKey("f"),
  label: "",
  amount: 0,
  per: "month",
  note: null,
  informational: false,
  appliesTo: { preset: "custom", from: "", to: "" },
});

const openCalendar = (e) => { try { e.currentTarget.showPicker(); } catch (_) { /* older browser */ } };

/** Native date input with our own label — avoids Chrome's ", January, 01, 2026". */
const SetupDayInput = ({ value, "aria-label": ariaLabel, ...rest }) => (
  <label className="gmbw-datefield">
    <span className={`gmbw-datefield-txt${value ? "" : " is-empty"}`} aria-hidden>
      {value ? formatSetupDay(value) : "Pick a date"}
    </span>
    <input
      type="date"
      className="gmbw-datefield-in"
      aria-label={ariaLabel}
      value={value}
      onClick={openCalendar}
      {...rest}
    />
  </label>
);

SetupDayInput.propTypes = {
  value: PropTypes.string,
  "aria-label": PropTypes.string,
};
SetupDayInput.defaultProps = { value: "", "aria-label": "Date" };

/** From → to range for a pay rule. Guarded so the range can never be invalid:
 *  - no future dates (max = company "today")
 *  - start can't sit after end (and vice-versa) — the other end is pulled to
 *    match instead of leaving a backwards range the server would read as empty. */
const AppliesToRange = ({ value, onChange }) => {
  const from = (value && value.from) || "";
  const to = (value && value.to) || "";
  const today = companyTodayStr();
  const noFuture = (d) => (d && d > today ? today : d);

  const setFrom = (raw) => {
    const f = noFuture(raw);
    // Start ahead of end → bump the end up so the range stays forward.
    const nextTo = to && f && f > to ? f : to;
    onChange({ preset: "custom", from: f, to: nextTo });
  };
  const setTo = (raw) => {
    const t = noFuture(raw);
    // End before start → pull the start down to match.
    const nextFrom = from && t && t < from ? t : from;
    onChange({ preset: "custom", from: nextFrom, to: t });
  };

  return (
    <>
      <span className="gmbw-glab">Date range</span>
      <div className="gmbw-applies">
        <div className="gmbw-range">
          <SetupDayInput
            aria-label="Start date"
            value={from}
            max={today}
            onChange={(e) => setFrom(e.target.value)}
          />
          <span className="gmbw-range-sep">→</span>
          <SetupDayInput
            aria-label="End date"
            value={to}
            min={from || undefined}
            max={today}
            onChange={(e) => setTo(e.target.value)}
          />
        </div>
      </div>
    </>
  );
};

AppliesToRange.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
};
AppliesToRange.defaultProps = { value: null };

const CombinedEarningsSetup = ({
  combinedPay,
  workspaceIds,
  workspaceNames = {},
  metricsByWs = {},
  onChange,
}) => {
  const wsIds = useMemo(
    () => (workspaceIds || []).map(String).filter(Boolean),
    [workspaceIds],
  );
  const pay = useMemo(
    () => sanitizeCombinedPay(combinedPay || emptyCombinedPay()),
    [combinedPay],
  );

  const write = (next) => onChange(sanitizeCombinedPay(next));

  const metricOptions = (wsId) => [
    { value: "", label: "— none —" },
    ...((metricsByWs[wsId] || []).map((m) => ({
      value: String(m.id),
      label: m.label || "Metric",
    }))),
  ];

  const patchBonus = (i, patch) => {
    write({
      ...pay,
      bonuses: pay.bonuses.map((b, j) => (j === i ? { ...b, ...patch } : b)),
    });
  };
  const patchCommission = (i, patch) => {
    write({
      ...pay,
      commissions: pay.commissions.map((c, j) => (j === i ? { ...c, ...patch } : c)),
    });
  };
  const patchFlat = (i, patch) => {
    write({
      ...pay,
      flats: pay.flats.map((f, j) => (j === i ? { ...f, ...patch } : f)),
    });
  };

  if (wsIds.length < 2) {
    return (
      <p className="gmbw-hint" style={{ margin: 0 }}>
        Pick two or more workspaces under Basics to combine their metrics into one pay rule.
      </p>
    );
  }

  return (
    <div className="gmbw-combined-pay">
      <p className="gmbw-hint" style={{ marginTop: 0 }}>
        One rule covers every workspace on this card. For each workspace, pick which metric
        counts — then sum or average them and apply the rate. Same idea as pairing metrics
        on Team Performance vs. The Yeti.
      </p>

      {/* ── Bonuses ─────────────────────────────────────────────────────── */}
      <div className="gmbw-sechead">
        <span>Bonuses</span>
        <span className="gmbw-count">
          {pay.bonuses.length === 1 ? "1 rule" : `${pay.bonuses.length} rules`}
        </span>
      </div>
      <div className="gmbw-mp">
        {pay.bonuses.map((b, i) => (
          <div className="gmbw-mp-col" key={b.key || i}>
            <div className="gmbw-mp-row">
              <input
                className="gmbw-mp-label"
                type="text"
                value={b.label || ""}
                placeholder="What to call it on the card"
                maxLength={80}
                onChange={(e) => patchBonus(i, { label: e.target.value })}
              />
              {/* Shows the real value, 0 included — collapsing 0 to "" here
                  used to fight typing an actual zero: the moment the digit
                  landed, the field re-derived from `amount === 0` and blanked
                  itself right back out. */}
              <span className="gmbw-inline" style={{ flex: "none", margin: 0 }}>
                <span>$</span>
                <input
                  type="number"
                  className="gmbw-in num"
                  style={{ width: 72 }}
                  min="0"
                  value={b.amount}
                  placeholder="1"
                  onChange={(e) => patchBonus(i, { amount: Number(e.target.value) || 0 })}
                />
              </span>
              <button
                type="button"
                className="gmbw-mp-x"
                title="Remove rule"
                onClick={() => write({ ...pay, bonuses: pay.bonuses.filter((_, j) => j !== i) })}
              >
                ×
              </button>
            </div>

            {wsIds.map((wsId) => (
              <div className="gmbw-mp-src" key={wsId}>
                <span className="gmbw-mp-ws">{workspaceNames[wsId] || "Workspace"}</span>
                <div className="gmbw-mp-src-fields">
                  <LightSelect
                    width="100%"
                    height={34}
                    fontSize={12.5}
                    radius={8}
                    menuWidth={300}
                    placeholder="— none —"
                    value={b.sources?.[wsId] || ""}
                    options={metricOptions(wsId)}
                    onChange={(o) => patchBonus(i, {
                      sources: { ...(b.sources || {}), [wsId]: o.value },
                    })}
                  />
                </div>
              </div>
            ))}

            <div className="gmbw-mp-mode">
              <LightSelect
                width="100%"
                height={34}
                fontSize={12.5}
                radius={8}
                value={b.mode === "avg" ? "avg" : "sum"}
                options={[
                  { value: "sum", label: "Sum across workspaces" },
                  { value: "avg", label: "Average across workspaces" },
                ]}
                onChange={(o) => patchBonus(i, { mode: o.value })}
              />
            </div>
            <AppliesToRange
              value={b.appliesTo}
              onChange={(appliesTo) => patchBonus(i, { appliesTo })}
            />
          </div>
        ))}
        <button
          type="button"
          className="gmbw-mp-add"
          onClick={() => write({ ...pay, bonuses: [...pay.bonuses, blankBonus(wsIds)] })}
        >
          + Add a bonus
        </button>
      </div>

      {/* ── Commission ──────────────────────────────────────────────────── */}
      <div className="gmbw-sechead gap">
        <span>Commission</span>
        <span className="gmbw-count">
          {pay.commissions.length === 1 ? "1 rule" : `${pay.commissions.length} rules`}
        </span>
      </div>
      <div className="gmbw-mp">
        {pay.commissions.map((c, i) => (
          <div className="gmbw-mp-col" key={c.key || i}>
            <div className="gmbw-mp-row">
              <input
                className="gmbw-mp-label"
                type="text"
                value={c.label || ""}
                placeholder="What to call it on the card"
                maxLength={80}
                onChange={(e) => patchCommission(i, { label: e.target.value })}
              />
              <span className="gmbw-inline" style={{ flex: "none", margin: 0 }}>
                <input
                  type="number"
                  className="gmbw-in num"
                  style={{ width: 64 }}
                  min="0"
                  value={c.pct}
                  placeholder="1"
                  onChange={(e) => patchCommission(i, { pct: Number(e.target.value) || 0 })}
                />
                <span>%</span>
              </span>
              <button
                type="button"
                className="gmbw-mp-x"
                title="Remove rule"
                onClick={() => write({
                  ...pay,
                  commissions: pay.commissions.filter((_, j) => j !== i),
                })}
              >
                ×
              </button>
            </div>
            {wsIds.map((wsId) => (
              <div className="gmbw-mp-src" key={wsId}>
                <span className="gmbw-mp-ws">{workspaceNames[wsId] || "Workspace"}</span>
                <div className="gmbw-mp-src-fields">
                  <LightSelect
                    width="100%"
                    height={34}
                    fontSize={12.5}
                    radius={8}
                    menuWidth={300}
                    placeholder="— none —"
                    value={c.sources?.[wsId] || ""}
                    options={metricOptions(wsId)}
                    onChange={(o) => patchCommission(i, {
                      sources: { ...(c.sources || {}), [wsId]: o.value },
                    })}
                  />
                </div>
              </div>
            ))}
            <div className="gmbw-mp-mode">
              <LightSelect
                width="100%"
                height={34}
                fontSize={12.5}
                radius={8}
                value={c.mode === "avg" ? "avg" : "sum"}
                options={[
                  { value: "sum", label: "Sum across workspaces" },
                  { value: "avg", label: "Average across workspaces" },
                ]}
                onChange={(o) => patchCommission(i, { mode: o.value })}
              />
            </div>
            <AppliesToRange
              value={c.appliesTo}
              onChange={(appliesTo) => patchCommission(i, { appliesTo })}
            />
          </div>
        ))}
        <button
          type="button"
          className="gmbw-mp-add"
          onClick={() => write({
            ...pay,
            commissions: [...pay.commissions, blankCommission(wsIds)],
          })}
        >
          + Add a commission
        </button>
      </div>

      {/* ── Flat bonuses (shared, not per-ws metrics) ───────────────────── */}
      <div className="gmbw-sechead gap">
        <span>Flat bonuses</span>
        <span className="gmbw-count">
          {pay.flats.length === 1 ? "1 rule" : `${pay.flats.length} rules`}
        </span>
      </div>
      <div className="gmbw-mp">
        {pay.flats.map((f, i) => (
          <div className="gmbw-mp-col" key={f.key || i}>
            <div className="gmbw-mp-row">
              <input
                className="gmbw-mp-label"
                type="text"
                value={f.label || ""}
                placeholder="Name"
                maxLength={80}
                onChange={(e) => patchFlat(i, { label: e.target.value })}
              />
              <span className="gmbw-inline" style={{ flex: "none", margin: 0 }}>
                <span>$</span>
                <input
                  type="number"
                  className="gmbw-in num"
                  style={{ width: 72 }}
                  min="0"
                  value={f.amount}
                  placeholder="500"
                  onChange={(e) => patchFlat(i, { amount: Number(e.target.value) || 0 })}
                />
              </span>
              <button
                type="button"
                className="gmbw-mp-x"
                title="Remove rule"
                onClick={() => write({ ...pay, flats: pay.flats.filter((_, j) => j !== i) })}
              >
                ×
              </button>
            </div>
            <AppliesToRange
              value={f.appliesTo}
              onChange={(appliesTo) => patchFlat(i, { appliesTo })}
            />
          </div>
        ))}
        <button
          type="button"
          className="gmbw-mp-add"
          onClick={() => write({ ...pay, flats: [...pay.flats, blankFlat()] })}
        >
          + Add a flat bonus
        </button>
      </div>

      <p className="gmbw-hint">
        These rules apply only to this card on this dashboard — not each workspace homepage.
      </p>
    </div>
  );
};

CombinedEarningsSetup.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  combinedPay: PropTypes.object,
  workspaceIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  workspaceNames: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  metricsByWs: PropTypes.object,
  onChange: PropTypes.func.isRequired,
};

CombinedEarningsSetup.defaultProps = {
  combinedPay: null,
  workspaceNames: {},
  metricsByWs: {},
};

export default CombinedEarningsSetup;
